import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import CreateCampaignModal from './CreateCampaignModal';
import CampaignDetailsModal from './CampaignDetailsModal';
import CampaignInvitations from './CampaignInvitations';
import { campaignsAPI, liveSessionsAPI } from '../../services/api';
import './MyCampaigns.css';

function Campaigns() {
  const { user } = useAuth();

  const isGMish = useMemo(() => {
    const role = (user?.role || '').toString().trim().toUpperCase();
    return role === 'GM' || role === 'GAME MASTER' || role === 'GAMEMASTER' || role === 'ADMIN';
  }, [user?.role]);

  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [detailsCampaignId, setDetailsCampaignId] = useState(null);
  const [leavingId, setLeavingId] = useState(null);

  // campaignId -> session (or null)
  const [activeSessionByCampaign, setActiveSessionByCampaign] = useState({});
  const pollRef = useRef(null);

  const refreshCampaigns = async () => {
    const res = await campaignsAPI.getMyCampaigns();
    setCampaigns(res.data?.campaigns || []);
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await campaignsAPI.getMyCampaigns();
        if (!isMounted) return;
        setCampaigns(res.data?.campaigns || []);
      } catch (e) {
        if (!isMounted) return;
        console.error('Failed to load campaigns:', e);
        setError(e?.response?.data?.error || 'Failed to load campaigns');
      } finally {
        if (!isMounted) return;
        setIsLoading(false);
      }
    };

    load();
    return () => { isMounted = false; };
  }, []);

  const handleCreateCampaign = async (payload) => {
    const res = await campaignsAPI.createCampaign({
      name: payload.name,
      description: payload.description
    });

    const created = res.data?.campaign;
    if (created) setCampaigns((prev) => [created, ...prev]);
  };

  const handleUpdatedCampaign = (updated) => {
    if (!updated?.id) return;
    setCampaigns((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
  };

  const isOwnerGMFor = (campaign) => {
    const gmId = campaign?.gmId;
    if (!gmId || !user?.id) return false;
    return isGMish && String(gmId).toLowerCase() === String(user.id).toLowerCase();
  };

  const launchPlaySession = (campaignOrId) => {
    const id = typeof campaignOrId === 'string'
      ? campaignOrId
      : (campaignOrId?.id || detailsCampaignId);

    if (!id) return;

    // IMPORTANT: PlaySession checks ?launch=1 to call liveSessionsAPI.launch()
    window.open(`/play/${id}?launch=1`, '_blank', 'noopener,noreferrer');
  };

  const joinActiveSession = (campaignId) => {
    if (!campaignId) return;
    window.open(`/play/${campaignId}`, '_blank', 'noopener,noreferrer');
  };

  const handleViewDetails = (campaignId) => {
    if (isGMish) {
      window.open(`/campaign-editor/${campaignId}`, '_blank', 'noopener,noreferrer');
    } else {
      setDetailsCampaignId(campaignId);
    }
  };

  const handleCharacterCreatedOrSelected = async () => {
    try {
      await refreshCampaigns();
    } catch (err) {
      console.error('Failed to refresh campaigns:', err);
    }
  };

  const handleLeaveCampaign = async (campaign) => {
    const campaignId = campaign?.id;
    if (!campaignId) return;

    const ok = window.confirm(`Leave "${campaign.name}"? This will remove your character from the campaign.`);
    if (!ok) return;

    try {
      setLeavingId(campaignId);
      setError(null);
      await campaignsAPI.leaveCampaign(campaignId);
      await refreshCampaigns();
    } catch (e) {
      console.error('Failed to leave campaign:', e);
      setError(e?.response?.data?.error || 'Failed to leave campaign');
    } finally {
      setLeavingId(null);
    }
  };

  // ---- Active session polling (so players see Join button after GM launches) ----
  useEffect(() => {
    const ids = (campaigns || []).map(c => c.id).filter(Boolean);
    if (ids.length === 0) {
      setActiveSessionByCampaign({});
      return;
    }

    let cancelled = false;

    const fetchActive = async () => {
      try {
        const pairs = await Promise.all(ids.map(async (cid) => {
          try {
            const res = await liveSessionsAPI.getActive(cid);
            return [cid, res.data?.session || null];
          } catch (e) {
            // Treat any error as "no active session" (includes 404 / not active)
            return [cid, null];
          }
        }));

        if (cancelled) return;

        const next = {};
        for (const [cid, session] of pairs) next[cid] = session;
        setActiveSessionByCampaign(next);
      } catch {
        // no-op; keep last state
      }
    };

    // initial + polling
    fetchActive();

    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(fetchActive, 12000);

    return () => {
      cancelled = true;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [campaigns]);

  return (
    <div className="campaigns-section my-campaigns-page">
      <CampaignInvitations onCharacterCreated={handleCharacterCreatedOrSelected} />

      <div className="section-header">
        <h2>My Campaigns</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {isGMish && (
            <button className="btn-primary" onClick={() => setIsCreateOpen(true)}>
              + Create Campaign
            </button>
          )}
          <button className="btn-primary">Join New Campaign</button>
        </div>
      </div>

      {error && (
        <div className="empty-state" style={{ marginBottom: 12 }}>
          <p>{error}</p>
        </div>
      )}

      <CreateCampaignModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreateCampaign}
      />

      <CampaignDetailsModal
        isOpen={!!detailsCampaignId}
        campaignId={detailsCampaignId}
        onClose={() => setDetailsCampaignId(null)}
        onUpdated={handleUpdatedCampaign}
        onLaunch={launchPlaySession}
      />

      {isLoading ? (
        <div className="empty-state">
          <p>Loading your campaigns...</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="empty-state">
          <p>You're not part of any campaigns yet.</p>
          <p className="hint">Join a campaign to start your journey in The Ascended!</p>
        </div>
      ) : (
        <div className="campaigns-grid">
          {campaigns.map((campaign) => {
            const ownerGM = isOwnerGMFor(campaign);
            const activeSession = activeSessionByCampaign?.[campaign.id] || null;

            return (
              <div key={campaign.id} className="campaign-card">
                <div className="campaign-header">
                  <h3>{campaign.name}</h3>
                  <span className="campaign-badge">
                    {activeSession ? 'LIVE' : (campaign.status || 'active')}
                  </span>
                </div>

                <div className="campaign-details">
                  <div className="detail-row">
                    <span className="detail-label">Game Master:</span>
                    <span className="detail-value">{campaign.gmName}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Players:</span>
                    <span className="detail-value">{campaign.playerCount}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Last Session:</span>
                    <span className="detail-value">
                      {campaign.lastSession
                        ? new Date(campaign.lastSession).toLocaleDateString()
                        : 'Not yet'}
                    </span>
                  </div>
                </div>

                <div className="campaign-actions">
                  {/* GM owner buttons */}
                  {ownerGM ? (
                    <>
                      <button
                        className="btn-secondary"
                        onClick={() => handleViewDetails(campaign.id)}
                      >
                        Campaign Editor
                      </button>

                      <button
                        className="btn-primary"
                        onClick={() => launchPlaySession(campaign)}
                        title="Launch an active play session"
                      >
                        Launch Campaign
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Player buttons (3-slot layout: View / Join (when live) / Leave) */}
                      <button
                        className="btn-secondary"
                        onClick={() => handleViewDetails(campaign.id)}
                        disabled={leavingId === campaign.id}
                      >
                        View Details
                      </button>

                      {activeSession ? (
                        <button
                          className="btn-secondary btn-join-active-session"
                          onClick={() => joinActiveSession(campaign.id)}
                          title="Join the active live session"
                        >
                          Join Active Session
                        </button>
                      ) : (
                        <div className="campaign-action-placeholder" aria-hidden="true" />
                      )}

                      <button
                        className="btn-secondary"
                        onClick={() => handleLeaveCampaign(campaign)}
                        disabled={leavingId === campaign.id}
                        title={leavingId === campaign.id ? 'Leaving...' : 'Leave this campaign'}
                      >
                        {leavingId === campaign.id ? 'Leaving...' : 'Leave Campaign'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Campaigns;
